import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ExternalLink, User, Music, Calendar, Edit, Archive, RotateCcw, X, Loader2,
  CheckCircle, TrendingUp, TrendingDown, DollarSign, Target, BarChart3,
  Clock, Users, Disc, PlayCircle, ListMusic,
  FileText, Hash, Globe, Sparkles, ChevronRight, RefreshCw, Zap, Plus, Search, Trash2
} from 'lucide-react';
import { BaseModal, ModalBody, ModalSection } from './index';
import { ArtistAvatar } from '../artists/ArtistAvatar';
import type { Artist, Contact, UpdateArtistData, CreateArtistData, SpotifyArtist, Campaign, Release } from '../../../shared/types/entities';
import type { ReportRecord } from '../../../shared/types/reports';
import { formatDate, formatCurrency, formatNumber } from '../../../shared/utils';
import { toast } from 'sonner';
import { spotifyService } from '../../../backend/services/spotify';
import { campaignService } from '../../../backend/services/campaigns';
import { metaAdsService } from '../../../backend/services/metaAds';
import { reportsService } from '../../../backend/services/reports';
import { supabase } from '../../../backend/services/supabase';
import { useAuth } from '../../hooks/useAuth';
import { useEffectivePermissions } from '../../hooks/useEffectivePermissions';

interface EnhancedArtistModalProps {
  isOpen: boolean;
  onClose: () => void;
  artist: Artist | null;
  clients: Contact[];
  mode?: 'view' | 'edit' | 'create';
  onUpdate: (id: string, updates: UpdateArtistData, options?: { optimistic?: boolean }) => Promise<Artist>;
  onArchive?: (id: string) => Promise<void>;
  onRestore?: (id: string) => Promise<void>;
  onCreate?: (data: CreateArtistData) => Promise<Artist>;
  onCreated?: (artist: Artist) => void;
  initialCreateData?: Partial<CreateArtistData>;
}

type WizardStep = 'spotify_url' | 'review_data' | 'contact' | 'releases' | 'campaigns' | 'overview';
type TabType = 'profile' | 'overview' | 'releases' | 'campaigns' | 'reports' | 'contacts';
type FieldState = 'idle' | 'saving' | 'saved' | 'error';

interface TrendData {
  value: number;
  timestamp: string;
}

interface ExecutiveMetrics {
  totalBudget: number;
  activeCampaigns: number;
  totalStreams: number;
  streamGrowth: number;
  avgROI: number;
  performanceScore: number;
  monthlyListeners?: number;
  playlistAdds?: number;
  saveRate?: number;
  trends?: {
    streams: TrendData[];
    listeners: TrendData[];
    engagement: TrendData[];
  };
}

// Loading Skeleton component
const Skeleton: React.FC<{
  className?: string;
  variant?: 'text' | 'rectangular' | 'circular';
}> = ({ className = '', variant = 'text' }) => {
  const baseClasses = 'animate-pulse bg-gray-200 dark:bg-gray-700';
  const variantClasses = {
    text: 'h-4 rounded',
    rectangular: 'rounded-lg',
    circular: 'rounded-full'
  };
  
  return (
    <div className={`${baseClasses} ${variantClasses[variant]} ${className}`} />
  );
};

// Smart Defaults Card
const SmartDefaultsCard: React.FC<{
  defaults: NonNullable<typeof smartDefaults>;
  onClose: () => void;
}> = ({ defaults, onClose }) => (
  <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-indigo-900/20 dark:to-indigo-800/20 
                rounded-xl p-4 border border-indigo-200 dark:border-indigo-800">
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center">
        <Sparkles className="w-5 h-5 text-indigo-600 dark:text-indigo-400 mr-2" />
        <h3 className="text-sm font-medium text-indigo-600 dark:text-indigo-400">
          Genre Insights
        </h3>
      </div>
      <button
        onClick={onClose}
        className="text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
    
    <div className="space-y-3">
      <p className="text-sm text-gray-600 dark:text-gray-400">
        Average metrics for top performing artists in {artist?.genre}:
      </p>
      
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="text-xs text-gray-500 dark:text-gray-400">Monthly Streams</div>
          <div className="font-medium">{formatNumber(defaults.avgStreams)}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500 dark:text-gray-400">Followers</div>
          <div className="font-medium">{formatNumber(defaults.avgFollowers)}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500 dark:text-gray-400">Save Rate</div>
          <div className="font-medium">{defaults.avgSaveRate}%</div>
        </div>
        <div>
          <div className="text-xs text-gray-500 dark:text-gray-400">Performance</div>
          <div className="font-medium">{defaults.avgPerformance}/100</div>
        </div>
      </div>
      
      <div>
        <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">Similar Artists</div>
        <div className="space-y-1">
          {defaults.similarArtists.map(a => (
            <div key={a.id} className="flex items-center justify-between text-sm">
              <span>{a.name}</span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Score: {a.performance_score}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

// Metric Card Skeleton
const MetricCardSkeleton: React.FC<{ variant?: 'large' | 'small' }> = ({ variant = 'large' }) => (
  <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900/20 dark:to-gray-800/20 
                rounded-xl p-4 border border-gray-200 dark:border-gray-700">
    {variant === 'large' ? (
      <>
        <div className="flex items-center justify-between mb-2">
          <Skeleton className="w-5 h-5" variant="circular" />
          <Skeleton className="w-24 h-4" />
        </div>
        <Skeleton className="w-32 h-8 mb-2" />
        <Skeleton className="w-20 h-3" />
      </>
    ) : (
      <>
        <div className="flex items-center mb-1">
          <Skeleton className="w-4 h-4 mr-2" variant="circular" />
          <Skeleton className="w-24 h-4" />
        </div>
        <Skeleton className="w-24 h-6" />
      </>
    )}
  </div>
);

// Tooltip component
const Tooltip: React.FC<{
  content: React.ReactNode;
  children: React.ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
}> = ({ content, children, position = 'top' }) => {
  const [isVisible, setIsVisible] = useState(false);
  
  const positionClasses = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2'
  };
  
  return (
    <div className="relative inline-flex" onMouseEnter={() => setIsVisible(true)} onMouseLeave={() => setIsVisible(false)}>
      {children}
      {isVisible && (
        <div className={`absolute z-50 ${positionClasses[position]}`}>
          <div className="bg-gray-900 text-white text-sm rounded-lg py-1 px-2 max-w-xs">
            {content}
          </div>
        </div>
      )}
    </div>
  );
};

// Image Modal component
const ImageModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  imageUrl: string;
  artistName: string;
}> = ({ isOpen, onClose, imageUrl, artistName }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/80" onClick={onClose}>
      <div className="relative max-w-2xl max-h-[90vh] p-4">
        <button
          onClick={onClose}
          className="absolute -top-2 -right-2 p-2 bg-white dark:bg-gray-800 rounded-full shadow-lg
                   hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors z-10"
          aria-label="Close image"
        >
          <X className="h-4 w-4 text-gray-500 dark:text-gray-400" />
        </button>
        <img
          src={imageUrl}
          alt={artistName}
          className="max-w-full max-h-full rounded-lg shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        />
        <div className="text-center mt-4">
          <p className="text-white font-medium">{artistName}</p>
        </div>
      </div>
    </div>
  );
};

// Confirmation Dialog component
const ConfirmationDialog: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  type?: 'warning' | 'error' | 'info';
}> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  type = 'warning'
}) => {
  if (!isOpen) return null;
  
  const colors = {
    warning: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800',
    error: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
    info: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
  };
  
  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/50">
      <div className={`w-full max-w-md p-6 rounded-lg border ${colors[type]} shadow-lg`}>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          {title}
        </h3>
        <div className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          {message}
        </div>
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
          >
            {cancelLabel}
          </button>
          <button
            onClick={() => {
              onConfirm();
              onClose();
            }}
            className={`px-4 py-2 text-sm text-white rounded-lg ${
              type === 'warning' ? 'bg-yellow-600 hover:bg-yellow-700' :
              type === 'error' ? 'bg-red-600 hover:bg-red-700' :
              'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// Sparkline component
const Sparkline: React.FC<{
  data: TrendData[];
  color: string;
  height?: number;
}> = ({ data, color, height = 20 }) => {
  if (!data || data.length < 2) return null;
  
  const values = data.map(d => d.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min;
  
  const points = data.map((d, i) => {
    const x = (i / (data.length - 1)) * 100;
    const y = range === 0 ? 50 : ((d.value - min) / range) * 100;
    return `${x},${100 - y}`;
  }).join(' ');
  
  return (
    <svg
      className="overflow-visible"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      style={{ height: `${height}px`, width: '100%' }}
    >
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

interface PerformanceData {
  campaigns: Campaign[];
  totalSpend: number;
  totalReach: number;
  totalImpressions: number;
  avgCPM: number;
  avgCTR: number;
  topSongs: Array<{
    name: string;
    streams: number;
    listeners: number;
    saves: number;
    playlistAdds: number;
  }>;
}

interface ActivityLog {
  id: string;
  action: string;
  user: string;
  timestamp: string;
  details?: string;
}

export const EnhancedArtistModal: React.FC<EnhancedArtistModalProps> = ({
  isOpen,
  onClose,
  artist,
  clients,
  mode = 'view',
  onUpdate,
  onArchive,
  onRestore,
  onCreate,
  onCreated,
  initialCreateData
}) => {
  const { user } = useAuth();
  const { hasRole } = useEffectivePermissions();
  const canEdit = hasRole('admin') || hasRole('manager');
  
  const [activeTab, setActiveTab] = useState<TabType>('profile');
  const [currentMode, setCurrentMode] = useState<'view' | 'edit' | 'create'>(mode);
  const [currentStep, setCurrentStep] = useState<WizardStep>('overview');
  const [form, setForm] = useState<UpdateArtistData | CreateArtistData>({
    client_id: '',
    name: '',
    genre: '',
    spotify_url: '',
    image_url: ''
  });
  const [fieldState, setFieldState] = useState<Record<string, FieldState>>({});
  const [timeouts, setTimeouts] = useState<Record<string, NodeJS.Timeout>>({});

  // Data states
  const [spotifyData, setSpotifyData] = useState<SpotifyArtist | null>(null);
  const [executiveMetrics, setExecutiveMetrics] = useState<ExecutiveMetrics | null>(null);
  const [performanceData, setPerformanceData] = useState<PerformanceData | null>(null);
  const [releases, setReleases] = useState<Release[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [reports, setReports] = useState<ReportRecord[]>([]);
  const [spotifyTracks, setSpotifyTracks] = useState<{
    popular: any[];
    recent: any[];
    searchResults: any[];
  }>({
    popular: [],
    recent: [],
    searchResults: []
  });
  const [selectedSingles, setSelectedSingles] = useState<Set<string>>(new Set());
  const [previewReleaseId, setPreviewReleaseId] = useState<string | null>(null);
  const [singlesSearchQuery, setSinglesSearchQuery] = useState('');
  const [loadingSpotifySingles, setLoadingSpotifySingles] = useState(false);
  
  // Loading states
  const [loadingTab, setLoadingTab] = useState<TabType | null>(null);
  const [loadingStep, setLoadingStep] = useState<WizardStep | null>(null);
  const [syncingSpotify, setSyncingSpotify] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: React.ReactNode;
    onConfirm: () => void;
    type?: 'warning' | 'error' | 'info';
    confirmLabel?: string;
    cancelLabel?: string;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });
  const [duplicateCheck, setDuplicateCheck] = useState<{
    isChecking: boolean;
    duplicates: Array<{
      id: string;
      name: string;
      type: 'spotify' | 'name';
    }>;
  }>({
    isChecking: false,
    duplicates: []
  });
  const [urlValidation, setUrlValidation] = useState<{
    isValid: boolean;
    message: string;
    artistId: string | null;
  }>({
    isValid: false,
    message: '',
    artistId: null
  });

  const extractSpotifyIdFromUrl = useCallback((url?: string | null) => {
    if (!url) return null;
    const match = url.match(/(track|album)\/([A-Za-z0-9]+)/);
    return match ? match[2] : null;
  }, []);

  // Validate Spotify URL
  const validateSpotifyUrl = useCallback((url: string) => {
    if (!url) {
      setUrlValidation({
        isValid: false,
        message: '',
        artistId: null
      });
      return;
    }

    try {
      const result = spotifyService.parseSpotifyUrl(url);
      if (!result) {
        setUrlValidation({
          isValid: false,
          message: 'Please enter a valid Spotify artist URL',
          artistId: null
        });
      } else if (result.type !== 'artist') {
        setUrlValidation({
          isValid: false,
          message: 'Please enter an artist URL (not an album, track, or playlist)',
          artistId: null
        });
      } else {
        setUrlValidation({
          isValid: true,
          message: 'Valid Spotify artist URL',
          artistId: result.id
        });
      }
    } catch (error) {
      setUrlValidation({
        isValid: false,
        message: 'Please enter a valid Spotify artist URL',
        artistId: null
      });
    }
  }, []);

  // Check for duplicate artists
  const checkDuplicates = useCallback(async (spotifyId: string, name: string) => {
    let query = supabase
      .from('artists')
      .select('id, name, spotify_url, spotify_id')
      .or(`spotify_id.eq.${spotifyId},name.ilike.${name}`);
    
    // Exclude current artist if updating
    if (artist?.id) {
      query = query.neq('id', artist.id);
    }
    
    const { data: existingArtists } = await query;
    
    if (existingArtists?.length) {
      const duplicates = existingArtists.map(a => ({
        id: a.id,
        name: a.name,
        type: a.spotify_id === spotifyId ? 'spotify' : 'name'
      }));
      
      return {
        hasDuplicates: true,
        duplicates
      };
    }
    
    return {
      hasDuplicates: false,
      duplicates: []
    };
  }, [artist?.id, setForm, setSpotifyData, setLastSyncedAt]);

  const handleDeleteRelease = useCallback((release: Release) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Remove Release',
      message: (
        <div className="space-y-2 text-sm">
          <p>Remove <strong>{release.title}</strong> from this artist?</p>
          <p className="text-gray-500 dark:text-gray-400">
            This will delete the release record but will not affect the track on Spotify.
          </p>
        </div>
      ),
      type: 'warning',
      confirmLabel: 'Remove Release',
      cancelLabel: 'Cancel',
      onConfirm: async () => {
        try {
          const { error } = await supabase
            .from('releases')
            .delete()
            .eq('id', release.id);

          if (error) throw error;

          setReleases(prev => prev.filter(r => r.id !== release.id));
          toast.success(`Removed ${release.title}`);
        } catch (error) {
          console.error('Failed to delete release:', error);
          toast.error('Failed to remove release');
        } finally {
          setConfirmDialog(prev => ({ ...prev, isOpen: false }));
        }
      }
    });
  }, [setConfirmDialog, setReleases]);

  // Save all pending changes
  const saveAllChanges = useCallback(async () => {
    if (!artist || currentMode !== 'edit') return;
    
    // Get all fields with pending changes
    const pendingFields = Object.entries(fieldState)
      .filter(([_, state]) => state === 'saving')
      .map(([field]) => field);
    
    if (pendingFields.length === 0) return;
    
    try {
      // Clear timeouts
      Object.values(timeouts).forEach(clearTimeout);
      setTimeouts({});
      
      // Create update object
      const updates = pendingFields.reduce((acc, field) => ({
        ...acc,
        [field]: (form as any)[field]
      }), {});
      
      // Update all fields
      await onUpdate(artist.id, updates as UpdateArtistData);
      
      // Set all fields to saved
      const newFieldState = pendingFields.reduce((acc, field) => ({
        ...acc,
        [field]: 'saved'
      }), {});
      setFieldState(prev => ({ ...prev, ...newFieldState }));
      
      // Clear saved states after delay
      setTimeout(() => {
        setFieldState(prev => {
          const newState = { ...prev };
          pendingFields.forEach(field => {
            if (newState[field] === 'saved') {
              newState[field] = 'idle';
            }
          });
          return newState;
        });
      }, 2000);
      
      toast.success('All changes saved');
    } catch (error) {
      console.error('Failed to save changes:', error);
      toast.error('Failed to save some changes');
    }
  }, [artist, currentMode, fieldState, form, onUpdate, timeouts]);

  const refreshArtistData = useCallback(async () => {
    if (!artist?.id) return null;

    try {
      const { data, error } = await supabase
        .from('artists')
        .select('*')
        .eq('id', artist.id)
        .single();

      if (error) throw error;

      if (data) {
        setForm(prev => ({
          ...prev,
          client_id: data.client_id,
          name: data.name,
          genre: data.genre || prev.genre,
          country: data.country || prev.country,
          type: data.type || prev.type,
          spotify_url: data.spotify_url || prev.spotify_url,
          image_url: data.image_url || prev.image_url,
        }));

        if (data.spotify_data) {
          setSpotifyData(data.spotify_data as SpotifyArtist);
          const syncedTimestamp = (data.spotify_data as any)?.syncedAt as string | undefined;
          setLastSyncedAt(syncedTimestamp || data.updated_at || null);
        }
      }

      return data;
    } catch (error) {
      console.error('Failed to refresh artist data:', error);
      return null;
    }
  }, [artist?.id]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        saveAllChanges();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [saveAllChanges]);

  // Get smart defaults from historical data
  const getSmartDefaults = useCallback(async () => {
    if (!artist?.genre) return null;
    
    try {
      // Get similar artists by genre
      const { data: similarArtists, error } = await supabase
        .from('artists')
        .select('*')
        .eq('genre', artist.genre)
        .neq('id', artist.id)
        .limit(20);

      if (error) {
        console.error('Failed to load similar artists for defaults:', error);
        return null;
      }

      if (!similarArtists?.length) return null;

      // Sort locally to avoid order-by errors on computed columns
      const sorted = [...similarArtists].sort((a, b) => {
        const scoreDiff = (b.performance_score || 0) - (a.performance_score || 0);
        if (scoreDiff !== 0) return scoreDiff;
        return (b.followers_count || 0) - (a.followers_count || 0);
      });

      const topArtists = sorted.slice(0, 5);

      // Calculate average metrics
      const avgMetrics = topArtists.reduce((acc, a) => ({
        followers_count: acc.followers_count + (a.followers_count || 0),
        performance_score: acc.performance_score + (a.performance_score || 0),
        monthly_streams: acc.monthly_streams + (a.monthly_streams || 0),
        save_rate: acc.save_rate + (a.save_rate || 0)
      }), {
        followers_count: 0,
        performance_score: 0,
        monthly_streams: 0,
        save_rate: 0
      });
      
      const count = topArtists.length;
      return {
        avgFollowers: Math.round(avgMetrics.followers_count / count),
        avgPerformance: Math.round(avgMetrics.performance_score / count),
        avgStreams: Math.round(avgMetrics.monthly_streams / count),
        avgSaveRate: (avgMetrics.save_rate / count).toFixed(1),
        similarArtists: topArtists.map(a => ({
          id: a.id,
          name: a.name,
          performance_score: a.performance_score
        }))
      };
    } catch (error) {
      console.error('Failed to get smart defaults:', error);
      return null;
    }
  }, [artist?.genre, artist?.id]);

  const formatSyncRelative = useCallback((timestamp: string) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const diffMinutes = Math.floor((Date.now() - date.getTime()) / 60000);

    if (diffMinutes < 1) return 'just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)}h ago`;
    return formatDate(timestamp);
  }, []);

  // Initialize form data
  useEffect(() => {
    if (artist) {
      setForm({
        client_id: artist.client_id,
        name: artist.name,
        genre: artist.genre || '',
        spotify_url: artist.spotify_url || '',
        spotify_id: artist.spotify_id || '',
        spotify_data: artist.spotify_data || undefined,
        image_url: artist.image_url || '',
        followers_count: artist.followers_count || undefined,
        lastfm_data: artist.lastfm_data || undefined,
        country: artist.country || '',
        type: artist.type || '',
        active: artist.active !== false,
        tags: artist.tags || [],
        urls: artist.urls || {}
      });
      setCurrentMode(mode);
      setCurrentStep('overview');
      setActiveTab('profile');
      if (artist.spotify_data) {
        setSpotifyData(artist.spotify_data as unknown as SpotifyArtist);
        const syncedTimestamp = (artist.spotify_data as any).syncedAt as string | undefined;
        setLastSyncedAt(syncedTimestamp || artist.updated_at || null);
      } else {
        setSpotifyData(null);
        setLastSyncedAt(null);
      }
      if (artist.spotify_url) {
        validateSpotifyUrl(artist.spotify_url);
      }
      
      // Load initial data
      loadExecutiveMetrics();
      loadTabData('profile');
    } else if (mode === 'create') {
      setForm({
        client_id: initialCreateData?.client_id || '',
        name: initialCreateData?.name || '',
        genre: initialCreateData?.genre || '',
        spotify_url: initialCreateData?.spotify_url || '',
        image_url: initialCreateData?.image_url || ''
      });
      setCurrentMode('create');
      setCurrentStep('spotify_url');
      setActiveTab('profile');
      setLastSyncedAt(null);
      if (initialCreateData?.spotify_url) {
        validateSpotifyUrl(initialCreateData.spotify_url);
      }
    }
  }, [artist, mode, initialCreateData, validateSpotifyUrl]);

  // Load executive metrics
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [showSmartDefaults, setShowSmartDefaults] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [smartDefaults, setSmartDefaults] = useState<{
    avgFollowers: number;
    avgPerformance: number;
    avgStreams: number;
    avgSaveRate: string;
    similarArtists: Array<{
      id: string;
      name: string;
      performance_score: number;
    }>;
  } | null>(null);
  
  // Load smart defaults when genre changes
  useEffect(() => {
    if (artist?.genre) {
      getSmartDefaults().then(setSmartDefaults);
    }
  }, [artist?.genre, getSmartDefaults]);
  
  const loadExecutiveMetrics = async () => {
    if (!artist) return;
    
    setLoadingMetrics(true);
    
    try {
      // Get campaigns for this artist
      const campaigns = await campaignService.getCampaignsByArtist(artist.id);
      const activeCampaigns = campaigns.filter(c => c.status === 'active').length;
      
      // Calculate total budget from campaigns
      const totalBudget = campaigns.reduce((sum, c) => sum + (c.budget || 0), 0);
      
      // Get Spotify metrics from uploads with trend data
      const { data: spotifyMetrics } = await supabase
        .from('spotify_song_metrics')
        .select('*')
        .eq('artist_id', artist.id)
        .order('recorded_at', { ascending: false })
        .limit(100);
        
      // Process trend data
      const trendData = spotifyMetrics?.reduce((acc, metric) => {
        const date = new Date(metric.recorded_at);
        const timestamp = date.toISOString();
        
        return {
          streams: [...acc.streams, { value: metric.streams || 0, timestamp }],
          listeners: [...acc.listeners, { value: metric.listeners || 0, timestamp }],
          engagement: [...acc.engagement, { 
            value: metric.saves && metric.streams ? (metric.saves / metric.streams) * 100 : 0,
            timestamp 
          }]
        };
      }, { streams: [], listeners: [], engagement: [] } as Record<string, TrendData[]>);
      
      const totalStreams = spotifyMetrics?.reduce((sum, m) => sum + (m.streams || 0), 0) || 0;
      const monthlyListeners = spotifyMetrics?.[0]?.listeners || artist.followers_count || 0;
      
      // Calculate growth (compare last 30 days to previous 30 days)
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
      const sixtyDaysAgo = new Date(Date.now() - 60 * 24 * 60 * 60 * 1000);
      
      const recentStreams = spotifyMetrics?.filter(m => 
        new Date(m.recorded_at) > thirtyDaysAgo
      ).reduce((sum, m) => sum + (m.streams || 0), 0) || 0;
      
      const previousStreams = spotifyMetrics?.filter(m => 
        new Date(m.recorded_at) <= thirtyDaysAgo && new Date(m.recorded_at) > sixtyDaysAgo
      ).reduce((sum, m) => sum + (m.streams || 0), 0) || 0;
      
      const streamGrowth = previousStreams > 0 
        ? ((recentStreams - previousStreams) / previousStreams) * 100 
        : 0;
      
      // Calculate performance score (0-100)
      const performanceScore = Math.min(100, Math.round(
        (activeCampaigns > 0 ? 20 : 0) +
        (totalStreams > 10000 ? 20 : totalStreams / 500) +
        (streamGrowth > 0 ? Math.min(30, streamGrowth) : 0) +
        (monthlyListeners > 1000 ? 30 : monthlyListeners / 33)
      ));
      
      // Get additional metrics
      const playlistAdds = spotifyMetrics?.reduce((sum, m) => sum + (m.playlist_adds || 0), 0) || 0;
      const totalSaves = spotifyMetrics?.reduce((sum, m) => sum + (m.saves || 0), 0) || 0;
      const saveRate = totalStreams > 0 ? (totalSaves / totalStreams) * 100 : 0;
      
      setExecutiveMetrics({
        totalBudget,
        activeCampaigns,
        totalStreams,
        streamGrowth,
        avgROI: totalBudget > 0 ? (totalStreams * 0.003) / totalBudget : 0, // Assuming $0.003 per stream
        performanceScore,
        monthlyListeners,
        playlistAdds,
        saveRate,
        trends: {
          streams: trendData?.streams.reverse() || [],
          listeners: trendData?.listeners.reverse() || [],
          engagement: trendData?.engagement.reverse() || []
        }
      });
    } catch (error) {
      console.error('Failed to load executive metrics:', error);
    } finally {
      setLoadingMetrics(false);
    }
  };

  // Load tab-specific data
  const loadTabData = async (tab: TabType) => {
    if (!artist || loadingTab === tab) return;
    
    setLoadingTab(tab);
    
    try {
      switch (tab) {
        case 'overview':
          await loadPerformanceData();
          break;
        case 'releases':
          await loadReleases();
          await loadSpotifySingles();
          break;
        case 'campaigns':
          await loadCampaigns();
          break;
        case 'reports':
          await loadReports();
          break;
        case 'profile':
        case 'contacts':
          // Profile and contacts data is already available
          break;
      }
    } catch (error) {
      console.error(`Failed to load ${tab} data:`, error);
    } finally {
      setLoadingTab(null);
    }
  };

  const loadPerformanceData = async () => {
    if (!artist) return;
    
    // Get campaigns
    const campaigns = await campaignService.getCampaignsByArtist(artist.id);
    
    // Get Meta insights
    const { data: insights } = await supabase
      .from('meta_insights')
      .select('*')
      .in('campaign_id', campaigns.map(c => c.external_campaign_id || '').filter(Boolean))
      .order('date_start', { ascending: false });
    
    // Get Spotify metrics
    const { data: spotifyMetrics } = await supabase
      .from('spotify_song_metrics')
      .select('*')
      .eq('artist_id', artist.id)
      .order('streams', { ascending: false })
      .limit(10);
    
    // Calculate totals
    const totalSpend = insights?.reduce((sum, i) => sum + (parseFloat(i.spend) || 0), 0) || 0;
    const totalReach = insights?.reduce((sum, i) => sum + (i.reach || 0), 0) || 0;
    const totalImpressions = insights?.reduce((sum, i) => sum + (i.impressions || 0), 0) || 0;
    const totalClicks = insights?.reduce((sum, i) => sum + (i.clicks || 0), 0) || 0;
    
    setPerformanceData({
      campaigns,
      totalSpend,
      totalReach,
      totalImpressions,
      avgCPM: totalImpressions > 0 ? (totalSpend / totalImpressions) * 1000 : 0,
      avgCTR: totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0,
      topSongs: spotifyMetrics?.map(m => ({
        name: m.song_name,
        streams: m.streams || 0,
        listeners: m.listeners || 0,
        saves: m.saves || 0,
        playlistAdds: m.playlist_adds || 0
      })) || []
    });
  };

  const loadReleases = async () => {
    if (!artist && !urlValidation.artistId) return;
    
    setLoadingStep('releases');
    try {
    const { data } = await supabase
      .from('releases')
      .select('*')
        .eq('artist_id', artist?.id || urlValidation.artistId)
      .order('release_date', { ascending: false });
    
    if (data) {
      const unique = new Map<string, Release>();
      data.forEach(release => {
        const trackId = (release.spotify_data as any)?.id || extractSpotifyIdFromUrl(release.spotify_url);
        const key = trackId || `${(release.title || '').toLowerCase()}|${release.release_date || ''}`;
        if (!unique.has(key)) {
          unique.set(key, release);
        }
      });
      setReleases(Array.from(unique.values()));
    } else {
      setReleases([]);
    }
    } catch (error) {
      console.error('Failed to load releases:', error);
      toast.error('Failed to load releases');
    } finally {
      setLoadingStep(null);
    }
  };

  const loadCampaigns = async () => {
    if (!artist) return;
    
    try {
      const campaignData = await campaignService.getCampaignsByArtist(artist.id);
      setCampaigns(campaignData || []);
    } catch (error) {
      console.error('Failed to load campaigns:', error);
      toast.error('Failed to load campaigns');
    }
  };

  const loadSpotifySingles = async () => {
    if (!artist?.spotify_id && !urlValidation.artistId) return;
    
    setLoadingSpotifySingles(true);
    try {
      const artistId = artist?.spotify_id || urlValidation.artistId;
      if (!artistId) return;
      
      // Call our API endpoint to get both popular and recent tracks
      const response = await fetch(`/api/spotify-artist-albums?artist_id=${encodeURIComponent(artistId)}`);
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      
      const data = await response.json();
      
      setSpotifyTracks({
        popular: data.popular || [],
        recent: data.recent || [],
        searchResults: []
      });
      
    } catch (error) {
      console.error('Failed to load Spotify tracks:', error);
      toast.error('Failed to load Spotify tracks');
    } finally {
      setLoadingSpotifySingles(false);
    }
  };

  const loadReports = async () => {
    if (!artist) return;

    try {
      const [reportArchive, artistCampaigns] = await Promise.all([
        reportsService.listReports(),
        campaignService.getCampaignsByArtist(artist.id)
      ]);

      setCampaigns(artistCampaigns || []);

      const campaignIdSet = new Set((artistCampaigns || []).map(c => c.id));
      const associatedClientId = form.client_id || artist.client_id || null;

      const filtered = (reportArchive || []).filter(report => {
        const matchesClient = associatedClientId ? report.clientId === associatedClientId : false;
        const matchesCampaign = report.campaignIds?.some(id => campaignIdSet.has(id));
        return matchesClient || matchesCampaign;
      });

      const sorted = filtered.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      setReports(sorted);
    } catch (error) {
      console.error('Failed to load reports:', error);
      toast.error('Failed to load reports');
    }
  };

  const loadActivityLogs = async () => {
    if (!artist) return;
    
    const { data } = await supabase
      .from('user_activities')
      .select('*, user:auth.users(email)')
      .eq('resource_type', 'artist')
      .eq('resource_id', artist.id)
      .order('created_at', { ascending: false })
      .limit(50);
    
    setActivityLogs(
      data?.map(log => ({
        id: log.id,
        action: log.action_type,
        user: log.user?.email || 'System',
        timestamp: log.created_at,
        details: log.details?.description
      })) || []
    );
  };

  // Handle Spotify sync
  const handleSpotifySync = async () => {
    if (!form.spotify_url || syncingSpotify || !urlValidation.isValid) return;
    
    setSyncingSpotify(true);
    try {
      const spotifyId = urlValidation.artistId;
      if (!spotifyId) throw new Error('Invalid Spotify URL');
      
      const artistData = await spotifyService.getArtist(spotifyId);
      
      // Only check for duplicates when creating new artists, not updating existing ones
      if (currentMode === 'create') {
        setDuplicateCheck(prev => ({ ...prev, isChecking: true }));
        const { hasDuplicates, duplicates } = await checkDuplicates(spotifyId, artistData.name);
        setDuplicateCheck({ isChecking: false, duplicates });
        
        if (hasDuplicates) {
          setConfirmDialog({
            isOpen: true,
            title: 'Duplicate Artist Found',
            message: (
              <div className="space-y-3">
                <p>We found existing artists that might be duplicates:</p>
                <ul className="space-y-2">
                  {duplicates.map(d => (
                    <li key={d.id} className="flex items-center">
                      <span className={`w-2 h-2 rounded-full mr-2 ${
                        d.type === 'spotify' ? 'bg-green-500' : 'bg-yellow-500'
                      }`} />
                      <span>{d.name}</span>
                      <span className="ml-2 text-xs text-gray-500">
                        ({d.type === 'spotify' ? 'Same Spotify ID' : 'Similar Name'})
                      </span>
                    </li>
                  ))}
                </ul>
                <p>Do you want to continue creating this artist?</p>
              </div>
            ),
            type: 'warning',
            confirmLabel: 'Create Anyway',
            cancelLabel: 'Cancel',
            onConfirm: () => {
              // Continue with import
              importSpotifyData(artistData);
            }
          });
          return;
        }
      }
      
      // No duplicates (or updating existing), continue with import
      await importSpotifyData(artistData);
      
    } catch (error) {
      console.error('Spotify sync failed:', error);
      toast.error('Failed to sync Spotify data');
    } finally {
      setSyncingSpotify(false);
    }
  };
  
  // Import Spotify data
  const importSpotifyData = async (artistData: SpotifyArtist) => {
    if (artist) {
      await onUpdate(artist.id, {
        spotify_data: artistData,
        image_url: artistData.images?.[0]?.url || artist.image_url,
        followers_count: artistData.followers?.total
      });
    }
    
    setSpotifyData(artistData);
    setLastSyncedAt(artistData.syncedAt || new Date().toISOString());
    
    // Update form with Spotify data
    setForm(prev => ({
      ...prev,
      name: artistData.name,
      genre: artistData.genres?.[0] || '',
      image_url: artistData.images?.[0]?.url || '',
      spotify_id: artistData.id,
      spotify_data: artistData,
      followers_count: artistData.followers?.total
    }));
    
    // Move to next step
    setCurrentStep('review_data');
    toast.success('Artist data imported successfully');
  };

  // Handle field changes with auto-save
  const handleFieldChange = useCallback((field: keyof UpdateArtistData, value: any) => {
    // Track previous value for undo
    const prevValue = (form as any)[field];
    
    // Update form state immediately for responsive UI
    setForm(prev => ({ ...prev, [field]: value }));
    
    // Validate Spotify URL
    if (field === 'spotify_url') {
      validateSpotifyUrl(value);
    }
    
    if ((currentMode === 'edit' && artist) || currentMode === 'create') {
      // Clear existing timeout
      if (timeouts[field as string]) {
        clearTimeout(timeouts[field as string]);
      }
      
      // Set saving state
      setFieldState(prev => ({ ...prev, [field]: 'saving' }));
      
      // Debounced save for edit mode
      const timeout = setTimeout(async () => {
        try {
          if (currentMode === 'edit' && artist) {
          await onUpdate(artist.id, { [field]: value } as UpdateArtistData, { optimistic: true });
          }
          
          setFieldState(prev => ({ ...prev, [field]: 'saved' }));
          
          // Clear saved state after delay
          setTimeout(() => {
            setFieldState(prev => ({ ...prev, [field]: 'idle' }));
          }, 2000);
          
        } catch (error) {
          console.error(`Failed to save ${field}:`, error);
          
          // Revert the value on error
          setForm(prev => ({ ...prev, [field]: prevValue }));
          
          setFieldState(prev => ({ ...prev, [field]: 'error' }));
          
          // Show error with undo option
          toast.error(
            <div className="flex flex-col space-y-2">
              <div>Failed to save {field}</div>
              <button
                onClick={() => handleFieldChange(field, value)}
                className="text-sm text-white underline hover:no-underline"
              >
                Retry
              </button>
            </div>
          );
        }
      }, 800);
      
      setTimeouts(prev => ({ ...prev, [field as string]: timeout }));
    }
  }, [artist, currentMode, onUpdate, timeouts, form]);

  // Tab configuration
  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'releases', label: 'Releases', icon: Disc },
    { id: 'campaigns', label: 'Campaigns', icon: Target },
    { id: 'reports', label: 'Reports', icon: FileText },
    { id: 'contacts', label: 'Contacts', icon: Users }
  ];

  // Render field with save indicator
  const renderField = (
    label: string,
    field: keyof UpdateArtistData,
    type: 'text' | 'select' | 'textarea' = 'text',
    options?: Array<{ value: string; label: string }>,
    editable: boolean = canEdit
  ) => {
    const state = fieldState[field as string] || 'idle';
    const value = (form as any)[field] || '';
    
    return (
      <div className="space-y-1">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {label}
        </label>
        <div className="relative">
          {type === 'select' ? (
            <select
              value={value}
              onChange={(e) => handleFieldChange(field, e.target.value)}
              disabled={currentMode === 'view'}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                       bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                       disabled:bg-gray-50 dark:disabled:bg-gray-900 disabled:cursor-not-allowed"
            >
              <option value="">Select {label}</option>
              {options?.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          ) : type === 'textarea' ? (
            <textarea
              value={value}
              onChange={(e) => handleFieldChange(field, e.target.value)}
              disabled={currentMode === 'view'}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                       bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                       disabled:bg-gray-50 dark:disabled:bg-gray-900 disabled:cursor-not-allowed"
            />
          ) : (
            <input
              type="text"
              value={value}
              onChange={(e) => handleFieldChange(field, e.target.value)}
              disabled={currentMode === 'view'}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                       bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                       disabled:bg-gray-50 dark:disabled:bg-gray-900 disabled:cursor-not-allowed"
            />
          )}
          
          {/* Save/Validation indicator */}
          {(currentMode === 'edit' && state !== 'idle') || (field === 'spotify_url' && form.spotify_url) ? (
            <div className="absolute right-2 top-1/2 -translate-y-1/2">
              {state === 'saving' && (
                <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
              )}
              {state === 'saved' && (
                <CheckCircle className="w-4 h-4 text-green-500" />
              )}
              {state === 'error' && (
                <X className="w-4 h-4 text-red-500" />
              )}
              {field === 'spotify_url' && form.spotify_url && (
                urlValidation.isValid ? (
                  <CheckCircle className="w-4 h-4 text-green-500" />
                ) : (
                  <X className="w-4 h-4 text-red-500" />
                )
              )}
            </div>
          ) : null}
        </div>
        {field === 'spotify_url' && form.spotify_url && urlValidation.message && (
          <div className={`mt-1 text-xs ${
            urlValidation.isValid ? 'text-green-600' : 'text-red-600'
          }`}>
            {urlValidation.message}
            </div>
        )}
      </div>
    );
  };

  // Render wizard content
  const renderWizardContent = () => {
    switch (currentStep) {
      case 'spotify_url':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Let's start with Spotify
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                Enter the Spotify URL of the artist to automatically import their data
              </p>
            </div>
            
            <div className="max-w-md mx-auto">
              {renderField('Spotify Artist URL', 'spotify_url', 'text', undefined, true)}
              
              {form.spotify_url && (
                <button
                  onClick={handleSpotifySync}
                  disabled={syncingSpotify}
                  className="w-full mt-4 px-4 py-2 bg-green-600 text-white rounded-lg
                           hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed
                           flex items-center justify-center"
                >
                  {syncingSpotify ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Importing Data...
                    </>
                  ) : (
                    <>
                      <Music className="w-4 h-4 mr-2" />
                      Import Artist Data
                    </>
                  )}
                </button>
          )}
        </div>
      </div>
    );
      
      case 'review_data':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Review Artist Data
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                Verify and edit the imported information
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              {renderField('Name', 'name', 'text', undefined, true)}
              {renderField('Genre', 'genre', 'text', undefined, true)}
              {renderField('Country', 'country', 'text', undefined, true)}
              {renderField('Type', 'type', 'select', [
                { value: 'Person', label: 'Solo Artist' },
                { value: 'Group', label: 'Band/Group' },
                { value: 'Orchestra', label: 'Orchestra' },
                { value: 'Choir', label: 'Choir' },
                { value: 'Character', label: 'Character' },
                { value: 'Other', label: 'Other' }
              ], true)}
            </div>
          </div>
        );
      
      case 'contact':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Associate with Client
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                Connect this artist with their record label or management
              </p>
            </div>
            
            {renderField('Client', 'client_id', 'select', 
              clients.map(c => ({ value: c.id, label: c.name })), true
            )}
          </div>
        );
      
      
      case 'campaigns':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Set Up Campaigns
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                Create and configure promotion campaigns
              </p>
            </div>
            
            {/* Campaign setup UI will go here */}
          </div>
        );
      
      case 'overview':
      default:
        return renderTabContent();
    }
  };

  // Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            {/* Metrics Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {loadingMetrics ? (
                <>
                  <MetricCardSkeleton />
                  <MetricCardSkeleton />
                  <MetricCardSkeleton />
                  <MetricCardSkeleton />
                </>
              ) : (
                <>
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 
                            rounded-xl p-4 border border-blue-200 dark:border-blue-800">
                <div className="flex items-center justify-between mb-2">
                  <DollarSign className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  <Tooltip
                    content="Total budget allocated across all campaigns for this artist"
                    position="top"
                  >
                    <span className="text-xs text-blue-600 dark:text-blue-400 cursor-help">
                      Total Budget
                    </span>
                  </Tooltip>
                </div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatCurrency(executiveMetrics?.totalBudget || 0)}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  Across all campaigns
                </div>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 
                            rounded-xl p-4 border border-green-200 dark:border-green-800">
                <div className="flex items-center justify-between mb-2">
                  <Target className="w-5 h-5 text-green-600 dark:text-green-400" />
                  <Tooltip
                    content="Number of campaigns currently running for this artist"
                    position="top"
                  >
                    <span className="text-xs text-green-600 dark:text-green-400 cursor-help">
                      Active Campaigns
                    </span>
                  </Tooltip>
                </div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {executiveMetrics?.activeCampaigns || 0}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  Currently running
                </div>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 
                            rounded-xl p-4 border border-purple-200 dark:border-purple-800">
                <div className="flex items-center justify-between mb-2">
                  <PlayCircle className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  <Tooltip
                    content="Total number of streams in the last 30 days across all tracks"
                    position="top"
                  >
                    <span className="text-xs text-purple-600 dark:text-purple-400 cursor-help">
                      Monthly Streams
                    </span>
                  </Tooltip>
                </div>
                <div className="flex items-baseline gap-2">
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatNumber(executiveMetrics?.totalStreams || 0)}
                </div>
                  {executiveMetrics?.streamGrowth !== undefined && (
                    <div className={`text-xs ${
                      executiveMetrics.streamGrowth > 0 
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                    }`}>
                      <div className="flex items-center">
                        {executiveMetrics.streamGrowth > 0 ? (
                          <TrendingUp className="w-3 h-3 mr-1" />
                        ) : (
                          <TrendingDown className="w-3 h-3 mr-1" />
                        )}
                        {executiveMetrics.streamGrowth > 0 ? '+' : ''}
                          {executiveMetrics.streamGrowth.toFixed(1)}%
                      </div>
                    </div>
                  )}
                </div>
                {executiveMetrics?.trends?.streams && (
                  <div className="mt-2">
                    <Sparkline
                      data={executiveMetrics.trends.streams}
                      color="rgb(147, 51, 234)"
                      height={24}
                    />
                  </div>
                )}
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/20 
                            rounded-xl p-4 border border-amber-200 dark:border-amber-800">
                <div className="flex items-center justify-between mb-2">
                  <Zap className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                  <Tooltip
                    content={
                      <div className="space-y-2">
                        <p>Overall performance score (0-100) based on:</p>
                        <ul className="list-disc list-inside text-xs">
                          <li>Active campaign count</li>
                          <li>Stream growth rate</li>
                          <li>Monthly listeners</li>
                          <li>Engagement metrics</li>
                        </ul>
                      </div>
                    }
                    position="top"
                  >
                    <span className="text-xs text-amber-600 dark:text-amber-400 cursor-help">
                      Performance Score
                    </span>
                  </Tooltip>
                </div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {executiveMetrics?.performanceScore || 0}/100
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 mt-2">
                  <div 
                    className="bg-amber-500 h-1.5 rounded-full transition-all duration-500"
                    style={{ width: `${executiveMetrics?.performanceScore || 0}%` }}
                  />
                </div>
              </div>
                </>
              )}
            </div>

            {/* Smart Defaults */}
            {smartDefaults && !showSmartDefaults && (
              <div className="flex justify-end">
                <button
                  onClick={() => setShowSmartDefaults(true)}
                  className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300
                           flex items-center"
                >
                  <Sparkles className="w-4 h-4 mr-1" />
                  View Genre Insights
                </button>
              </div>
            )}
            
            {smartDefaults && showSmartDefaults && (
              <SmartDefaultsCard
                defaults={smartDefaults}
                onClose={() => setShowSmartDefaults(false)}
              />
            )}

            {/* Additional Metrics */}
            <div className="grid grid-cols-3 gap-4">
              {loadingMetrics ? (
                <>
                  <MetricCardSkeleton variant="small" />
                  <MetricCardSkeleton variant="small" />
                  <MetricCardSkeleton variant="small" />
                </>
              ) : (
                <>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center text-sm text-gray-600 dark:text-gray-400 mb-1">
                  <Users className="w-4 h-4 mr-2" />
                  <Tooltip
                    content="Unique listeners in the last 30 days across all tracks"
                    position="top"
                  >
                    <span className="cursor-help">Monthly Listeners</span>
                  </Tooltip>
                </div>
                <div className="text-xl font-semibold text-gray-900 dark:text-white">
                  {formatNumber(executiveMetrics?.monthlyListeners || 0)}
                </div>
                {executiveMetrics?.trends?.listeners && (
                  <div className="mt-2">
                    <Sparkline
                      data={executiveMetrics.trends.listeners}
                      color="rgb(59, 130, 246)"
                      height={16}
                    />
                  </div>
                )}
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center text-sm text-gray-600 dark:text-gray-400 mb-1">
                  <ListMusic className="w-4 h-4 mr-2" />
                  <Tooltip
                    content="Number of times tracks were added to playlists in the last 30 days"
                    position="top"
                  >
                    <span className="cursor-help">Playlist Adds</span>
                  </Tooltip>
                </div>
                <div className="text-xl font-semibold text-gray-900 dark:text-white">
                  {formatNumber(executiveMetrics?.playlistAdds || 0)}
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center text-sm text-gray-600 dark:text-gray-400 mb-1">
                  <Sparkles className="w-4 h-4 mr-2" />
                  <Tooltip
                    content={
                      <div className="space-y-2">
                        <p>Percentage of streams that result in a track save</p>
                        <p className="text-xs opacity-75">Higher rates indicate stronger listener engagement</p>
                      </div>
                    }
                    position="top"
                  >
                    <span className="cursor-help">Save Rate</span>
                  </Tooltip>
                </div>
                <div className="text-xl font-semibold text-gray-900 dark:text-white">
                  {(executiveMetrics?.saveRate || 0).toFixed(1)}%
                </div>
                {executiveMetrics?.trends?.engagement && (
                  <div className="mt-2">
                    <Sparkline
                      data={executiveMetrics.trends.engagement}
                      color="rgb(234, 179, 8)"
                      height={16}
                    />
              </div>
                )}
              </div>
                </>
              )}
            </div>
          </div>
        );

      case 'profile':
        return (
          <div className="space-y-4">
            {/* Spotify Integration */}
            <div className="space-y-4">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center">
                  <Music className="w-4 h-4 mr-2 text-green-600" />
                  Spotify Integration
                </h3>
                {artist?.spotify_url && (
                  <div className="flex flex-col items-end gap-1">
                    <button
                      onClick={handleSpotifySync}
                      disabled={syncingSpotify}
                      className={`flex items-center px-3 py-1.5 text-sm font-medium text-white rounded-lg transition-all 
                               focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 
                               disabled:opacity-60 disabled:cursor-not-allowed ${
                                 syncingSpotify
                                   ? 'bg-green-500 cursor-wait animate-pulse'
                                   : 'bg-green-600 hover:bg-green-700'
                               }`}
                    >
                      {syncingSpotify ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4 mr-2" />
                      )}
                      {syncingSpotify ? 'Syncing...' : 'Sync Data'}
                    </button>
                    {lastSyncedAt && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        Last synced {formatSyncRelative(lastSyncedAt)}
                      </span>
                    )}
                  </div>
                )}
              </div>
              
              {renderField('Spotify URL', 'spotify_url', 'text', undefined, true)}
              
              {spotifyData && lastSyncedAt && (
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Last synced: {formatDate(lastSyncedAt)}
                </div>
              )}
            </div>

            {/* External Links */}
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center">
                <Globe className="w-4 h-4 mr-2" />
                External Links
              </h3>
              <div className="space-y-2">
                {artist?.spotify_url && (
                  <a
                    href={artist.spotify_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Spotify Artist Page
                  </a>
                )}
                {artist?.lastfm_data?.url && (
                  <a
                    href={artist.lastfm_data.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Last.fm Profile
                  </a>
                )}
              </div>
            </div>

            {/* Bio */}
            {artist?.lastfm_data?.bio && (
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                  Biography
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                  {artist.lastfm_data.bio.summary || artist.lastfm_data.bio.content}
                </p>
              </div>
            )}
          </div>
        );


      case 'releases':
        return (
          <div className="space-y-6">
            {loadingSpotifySingles ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                <span className="ml-2 text-gray-600">Loading Spotify tracks...</span>
              </div>
            ) : (
              <>
                {/* Import from Spotify Section - Only show in edit mode */}
                {currentMode === 'edit' && (
                  <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 
                                rounded-xl p-6 border border-green-200 dark:border-green-800">
                  <div className="flex items-center justify-between mb-4">
                <div>
                      <div className="flex items-center">
                        <Music className="w-5 h-5 text-green-600 dark:text-green-400 mr-2" />
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          Import from Spotify
                  </h3>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Select tracks to import and create campaigns for
                      </p>
                    </div>
                    {selectedSingles.size > 0 && (
                      <button
                        onClick={async () => {
                          if (!artist?.id) {
                            toast.error('Artist must be saved before importing tracks');
                            return;
                          }
                          
                          setLoadingSpotifySingles(true);
                          try {
                            // Get selected tracks
                            const selectedTracks = [
                              ...spotifyTracks.popular.filter(t => selectedSingles.has(t.id)),
                              ...spotifyTracks.recent.filter(t => selectedSingles.has(t.id))
                            ];
                            const uniqueTrackMap = new Map<string, any>();
                            selectedTracks.forEach(track => {
                              if (track?.id && !uniqueTrackMap.has(track.id)) {
                                uniqueTrackMap.set(track.id, track);
                              }
                            });

                            const existingTrackIds = new Set(
                              releases
                                .map(release => (release.spotify_data as any)?.id || extractSpotifyIdFromUrl(release.spotify_url))
                                .filter(Boolean) as string[]
                            );

                            const tracksToImport = Array.from(uniqueTrackMap.values()).filter(track => !existingTrackIds.has(track.id));

                            if (tracksToImport.length === 0) {
                              toast.info('All selected tracks are already imported');
                              return;
                            }

                            const skipped = selectedTracks.length - tracksToImport.length;
                            if (skipped > 0) {
                              toast.info(`Skipped ${skipped} track${skipped === 1 ? '' : 's'} already in your releases`);
                            }
                            
                            // Import tracks
                            const response = await fetch('/api/import-spotify-tracks', {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                              },
                              body: JSON.stringify({
                                artist_id: artist.id,
                                tracks: tracksToImport
                              })
                            });
                            
                            const payload = await response.json().catch(() => null);

                            if (!response.ok) {
                              const message = payload?.details || payload?.message || payload?.error || `Import failed (${response.status})`;
                              throw new Error(message);
                            }

                            const result = payload;
                            
                            // Clear selections and reload releases
                            setSelectedSingles(new Set());
                            await loadReleases();
                            
                            toast.success(`Successfully imported ${result.imported || tracksToImport.length} tracks!`);
                          } catch (error) {
                            console.error('Failed to import tracks:', error);
                            const message = error instanceof Error ? error.message : 'Failed to import tracks';
                            toast.error(message);
                          } finally {
                            setLoadingSpotifySingles(false);
                          }
                        }}
                        disabled={loadingSpotifySingles}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 
                                 flex items-center font-medium disabled:opacity-50"
                      >
                        {loadingSpotifySingles ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Importing...
                          </>
                        ) : (
                          <>
                            <Plus className="w-4 h-4 mr-2" />
                            Import Selected ({selectedSingles.size})
                          </>
                        )}
                      </button>
                    )}
                      </div>
                  
                  {/* Popular Singles */}
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3 flex items-center">
                      <TrendingUp className="w-4 h-4 mr-2 text-orange-500" />
                      Most Popular Tracks
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {spotifyTracks.popular.map(track => (
                        <div key={track.id} className={`rounded-lg border p-4 hover:shadow-md transition-all ${
                          selectedSingles.has(track.id)
                            ? 'border-green-500 bg-green-50 dark:bg-green-900/20 shadow-md'
                            : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                        }`}>
                          <div className="flex items-start space-x-3">
                            <div className="relative">
                              <img
                                src={track.album?.images?.[0]?.url || '/placeholder-album.png'}
                                alt={track.name}
                                className="w-16 h-16 rounded-lg object-cover"
                              />
                              <div className="absolute -top-2 -right-2">
                                <input
                                  type="checkbox"
                                  checked={selectedSingles.has(track.id)}
                                  onChange={(e) => {
                                    const newSelected = new Set(selectedSingles);
                                    if (e.target.checked) {
                                      newSelected.add(track.id);
                                    } else {
                                      newSelected.delete(track.id);
                                    }
                                    setSelectedSingles(newSelected);
                                  }}
                                  className="w-6 h-6 text-green-600 bg-white border-2 border-gray-300 
                                           rounded focus:ring-green-500 dark:focus:ring-green-600 
                                           dark:ring-offset-gray-800 focus:ring-2 shadow-sm"
                                />
                    </div>
                      </div>
                            <div className="flex-1 min-w-0">
                              <h5 className="font-medium text-gray-900 dark:text-white truncate">
                                {track.name}
                              </h5>
                              <div className="flex items-center gap-2 mt-1 text-xs text-gray-600 dark:text-gray-400">
                                <span className="px-1.5 py-0.5 bg-orange-100 dark:bg-orange-900/30 
                                               text-orange-700 dark:text-orange-400 rounded">
                                  {track.popularity}% popular
                                </span>
                                <span>{track.album?.name}</span>
                    </div>
                              
                              {/* Spotify Preview for Selected Items */}
                              {selectedSingles.has(track.id) && track.external_urls?.spotify && (
                                <div className="mt-2 rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-2">
                                  <iframe
                                    src={`https://open.spotify.com/embed/track/${track.id}`}
                                    width="100%"
                                    height="110"
                                    frameBorder="0"
                                    allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                                    loading="lazy"
                                    className="rounded"
                                  />
                                </div>
                              )}
                    </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>

                  {/* Recent Singles */}
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3 flex items-center">
                      <Calendar className="w-4 h-4 mr-2 text-blue-500" />
                      Most Recent Tracks
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {spotifyTracks.recent.map(track => (
                        <div key={track.id} className={`rounded-lg border p-4 hover:shadow-md transition-all ${
                          selectedSingles.has(track.id)
                            ? 'border-green-500 bg-green-50 dark:bg-green-900/20 shadow-md'
                            : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                        }`}>
                          <div className="flex items-start space-x-3">
                            <div className="relative">
                              <img
                                src={track.album?.images?.[0]?.url || '/placeholder-album.png'}
                                alt={track.name}
                                className="w-16 h-16 rounded-lg object-cover"
                              />
                              <div className="absolute -top-2 -right-2">
                                <input
                                  type="checkbox"
                                  checked={selectedSingles.has(track.id)}
                                  onChange={(e) => {
                                    const newSelected = new Set(selectedSingles);
                                    if (e.target.checked) {
                                      newSelected.add(track.id);
                                    } else {
                                      newSelected.delete(track.id);
                                    }
                                    setSelectedSingles(newSelected);
                                  }}
                                  className="w-6 h-6 text-green-600 bg-white border-2 border-gray-300 
                                           rounded focus:ring-green-500 dark:focus:ring-green-600 
                                           dark:ring-offset-gray-800 focus:ring-2 shadow-sm"
                                />
                            </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <h5 className="font-medium text-gray-900 dark:text-white truncate">
                                {track.name}
                              </h5>
                              <div className="flex items-center gap-2 mt-1 text-xs text-gray-600 dark:text-gray-400">
                                <span className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 
                                               text-blue-700 dark:text-blue-400 rounded">
                                  Recent
                                </span>
                                <span>{track.album?.name}</span>
                          </div>
                              
                              {/* Spotify Preview for Selected Items */}
                              {selectedSingles.has(track.id) && track.external_urls?.spotify && (
                                <div className="mt-2 rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-2">
                                  <iframe
                                    src={`https://open.spotify.com/embed/track/${track.id}`}
                                    width="100%"
                                    height="110"
                                    frameBorder="0"
                                    allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                                    loading="lazy"
                                    className="rounded"
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Search More Singles */}
                  <div className="border-t border-green-200 dark:border-green-800 pt-4">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="flex-1">
                        <input
                          type="text"
                          placeholder="Search artist's catalog for more tracks..."
                          value={singlesSearchQuery}
                          onChange={(e) => setSinglesSearchQuery(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                                   bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                                   placeholder-gray-500 dark:placeholder-gray-400"
                        />
                    </div>
                      <button
                        onClick={() => {
                          // TODO: Implement search functionality
                          toast.info('Search functionality coming soon!');
                        }}
                        className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
                      >
                        <Search className="w-4 h-4 mr-1" />
                        Search
                      </button>
                  </div>
          </div>
              </div>
                )}
                
                {/* Existing Releases in System */}
                {releases.length > 0 ? (
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                      <Disc className="w-5 h-5 mr-2 text-gray-600" />
                      Your Imported Tracks
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      Tracks you've already imported and can create campaigns for
                    </p>
                    <div className="space-y-3">
                      {releases.map(release => {
                        const artwork = (release.artwork_url as string) 
                          || (release.spotify_data as any)?.album?.images?.[0]?.url 
                          || (release.spotify_data as any)?.images?.[0]?.url;
                        const trackId = (release.spotify_data as any)?.id || extractSpotifyIdFromUrl(release.spotify_url);
                        const albumId = (release.spotify_data as any)?.album?.id || (release.spotify_url ? release.spotify_url.match(/album\/([A-Za-z0-9]+)/)?.[1] : null);
                        const embedUrl = trackId
                          ? `https://open.spotify.com/embed/track/${trackId}`
                          : albumId
                            ? `https://open.spotify.com/embed/album/${albumId}`
                            : null;

                        return (
                        <div
                          key={release.id}
                          className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden"
                        >
                          <div className="flex gap-3 p-3">
                            {artwork ? (
                              <img
                                src={artwork}
                                alt={release.title}
                                className="w-14 h-14 rounded-md object-cover flex-shrink-0"
                              />
                            ) : (
                              <div className="w-14 h-14 rounded-md bg-gray-100 dark:bg-gray-700 flex-shrink-0" />
                            )}
                            <div className="flex-1 min-w-0 space-y-2">
                              <div className="flex flex-wrap items-start gap-3">
                                <div className="flex-1 min-w-0">
                                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                                    {release.title}
                                  </h4>
                                  <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-600 dark:text-gray-400">
                                    <span className="uppercase tracking-wide text-gray-700 dark:text-gray-300">
                                      {release.release_type}
                                    </span>
                                    <span className="text-gray-300 dark:text-gray-600">|</span>
                                    <span>{formatDate(release.release_date)}</span>
                                    {release.label && (
                                      <>
                                        <span className="text-gray-300 dark:text-gray-600">|</span>
                                        <span className="truncate">{release.label}</span>
                                      </>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                  {release.spotify_url && (
                                    <a
                                      href={release.spotify_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center justify-center rounded-md border border-green-200 px-2 py-1 text-xs text-green-700 hover:bg-green-50 dark:border-green-800 dark:text-green-400 dark:hover:bg-green-900/20"
                                      title="Open in Spotify"
                                    >
                                      <ExternalLink className="w-3 h-3 mr-1" />
                                      Open
                                    </a>
                                  )}
                                  {embedUrl && (
                                    <button
                                      onClick={() =>
                                        setPreviewReleaseId(prev => (prev === release.id ? null : release.id))
                                      }
                                      className="inline-flex items-center rounded-md border border-gray-300 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                                    >
                                      {previewReleaseId === release.id ? 'Hide Preview' : 'Preview'}
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleDeleteRelease(release)}
                                    className="inline-flex items-center rounded-md bg-red-50 px-2 py-1 text-xs text-red-600 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/40"
                                  >
                                    <Trash2 className="w-3 h-3 mr-1" />
                                    Remove
                                  </button>
                                  <button
                                    onClick={() => {
                                      setActiveTab('campaigns');
                                    }}
                                    className="inline-flex items-center rounded-md bg-blue-600 px-2 py-1 text-xs font-medium text-white hover:bg-blue-700"
                                  >
                                    Create Campaign
                                  </button>
                                </div>
                              </div>
                              {embedUrl && previewReleaseId === release.id && (
                                <div className="rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-2">
                                  <iframe
                                    src={embedUrl}
                                    width="100%"
                                    height="120"
                                    frameBorder="0"
                                    allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                                    loading="lazy"
                                    className="rounded" 
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                    <Disc className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <h3 className="text-xl font-medium mb-2">No tracks imported yet</h3>
                    <p className="text-sm mb-6 max-w-md mx-auto">
                      Import tracks from Spotify to start creating campaigns and promoting this artist's music.
                    </p>
                    {currentMode === 'view' && (
                      <button 
                        onClick={() => setCurrentMode('edit')}
                        className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 
                                 flex items-center mx-auto font-medium"
                      >
                        <Plus className="w-5 h-5 mr-2" />
                        Import Tracks from Spotify
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        );

      case 'reports': {
        const associatedClientId = form.client_id || artist?.client_id;
        const associatedClient = associatedClientId
          ? clients.find(c => c.id === associatedClientId)
          : undefined;
        const campaignNameMap = new Map(campaigns.map(c => [c.id, c.name]));

        return (
          <div className="space-y-6">
            {loadingTab === 'reports' ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : reports.length > 0 ? (
              <div className="space-y-3">
                {reports.map(report => {
                  const campaignNames = (report.campaignIds || [])
                    .map(id => campaignNameMap.get(id) || 'Campaign')
                    .filter(Boolean);
                  const sources = Object.entries(report.include || {})
                    .filter(([, enabled]) => Boolean(enabled))
                    .map(([source]) => {
                      const labels: Record<string, string> = {
                        meta: 'Meta Ads',
                        spotify: 'Spotify',
                        hypeedit: 'Hypeddit'
                      };
                      return labels[source] || source;
                    });
                  const spend = Number((report.metrics as any)?.meta?.spend ?? NaN);
                  const impressions = Number((report.metrics as any)?.meta?.impressions ?? NaN);
                  const clicks = Number((report.metrics as any)?.meta?.clicks ?? NaN);
                  const createdAtLabel = formatDate(report.createdAt);
                  const periodLabel = report.startDate && report.endDate
                    ? `${formatDate(report.startDate)} – ${formatDate(report.endDate)}`
                    : 'Custom period';

                  return (
                    <div
                      key={report.id}
                      className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4"
                    >
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                        <div>
                          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                            <FileText className="w-3 h-3" />
                            {createdAtLabel}
                            {associatedClient && (
                              <>
                                <span>•</span>
                                <span>{associatedClient.name}</span>
                              </>
                            )}
                          </div>
                          <h4 className="mt-1 text-base font-semibold text-gray-900 dark:text-white">
                            {campaignNames[0] || 'Report'}
                          </h4>
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            {periodLabel}
                          </div>
                          {campaignNames.length > 1 && (
                            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                              {campaignNames.slice(0, 3).join(', ')}
                              {campaignNames.length > 3 && ` +${campaignNames.length - 3} more`}
                            </div>
                          )}
                          {sources.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2">
                              {sources.map(label => (
                                <span
                                  key={label}
                                  className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-full"
                                >
                                  {label}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex items-start gap-2 md:justify-end">
                          <a
                            href={`/reports?report=${encodeURIComponent(report.id)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center rounded-md border border-gray-300 dark:border-gray-600 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                          >
                            View in Reports
                          </a>
                        </div>
                      </div>

                      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div className="rounded-md bg-gray-50 dark:bg-gray-900/40 p-3">
                          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase">Spend</div>
                          <div className="text-sm font-semibold text-gray-900 dark:text-white">
                            {Number.isFinite(spend) ? formatCurrency(spend) : '—'}
                          </div>
                        </div>
                        <div className="rounded-md bg-gray-50 dark:bg-gray-900/40 p-3">
                          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase">Impressions</div>
                          <div className="text-sm font-semibold text-gray-900 dark:text-white">
                            {Number.isFinite(impressions) ? formatNumber(impressions) : '—'}
                          </div>
                        </div>
                        <div className="rounded-md bg-gray-50 dark:bg-gray-900/40 p-3">
                          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase">Clicks</div>
                          <div className="text-sm font-semibold text-gray-900 dark:text-white">
                            {Number.isFinite(clicks) ? formatNumber(clicks) : '—'}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <FileText className="w-14 h-14 mx-auto mb-4 text-gray-300" />
                <h3 className="text-xl font-medium mb-2">No reports yet</h3>
                <p className="text-sm mb-6 max-w-md mx-auto">
                  Generate a report from the Reports workspace and include this artist’s campaigns to see it here.
                </p>
                <a
                  href="/reports"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Open Reports Workspace
                </a>
              </div>
            )}
          </div>
        );
      }

      case 'campaigns':
        return (
          <div className="space-y-4">
            {loadingTab === 'campaigns' ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : campaigns.length > 0 ? (
              campaigns.map(campaign => (
                <div key={campaign.id} className="p-4 bg-white dark:bg-gray-800 rounded-lg border 
                                                 border-gray-200 dark:border-gray-700">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          {campaign.name}
                        </h4>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          campaign.status === 'active' 
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            : campaign.status === 'paused'
                            ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                            : 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400'
                        }`}>
                          {campaign.status}
                        </span>
            </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3">
            <div>
                          <div className="text-xs text-gray-600 dark:text-gray-400">Budget</div>
                          <div className="font-semibold text-gray-900 dark:text-white">
                            {formatCurrency(campaign.budget || 0)}
                </div>
              </div>
                        <div>
                          <div className="text-xs text-gray-600 dark:text-gray-400">Spend</div>
                          <div className="font-semibold text-gray-900 dark:text-white">
                            {formatCurrency(campaign.spend || 0)}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-600 dark:text-gray-400">Reach</div>
                          <div className="font-semibold text-gray-900 dark:text-white">
                            {formatNumber(campaign.reach || 0)}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-600 dark:text-gray-400">CPM</div>
                          <div className="font-semibold text-gray-900 dark:text-white">
                            {formatCurrency(campaign.cpm || 0)}
                          </div>
                        </div>
                      </div>
                      
                      {campaign.release_id && (
                        <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                          Release: {releases.find(r => r.id === campaign.release_id)?.title || 'Unknown'}
              </div>
              )}
            </div>

                    <div className="flex items-center gap-2">
                      <button className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700">
                        View Details
                      </button>
                      <button className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 
                                       text-gray-700 dark:text-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-800">
                        Edit
                      </button>
                </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <Target className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium mb-2">No campaigns yet</p>
                <p className="text-sm mb-4">Create your first campaign to start promoting this artist's releases.</p>
                <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                  Create Campaign
                </button>
              </div>
            )}
          </div>
        );

      case 'contacts': {
        const associatedClientId = form.client_id || artist?.client_id;
        const associatedClient = associatedClientId
          ? clients.find(c => c.id === associatedClientId)
          : undefined;
        const associatedClientPhoneHref = associatedClient?.phone
          ? associatedClient.phone.replace(/[^+\d]/g, '')
          : null;

        return (
          <div className="space-y-4">
            {/* Primary Contact */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                Primary Contact
                </h3>
              {currentMode === 'edit' || currentMode === 'create' ? (
                renderField('Associated Client', 'client_id', 'select', 
                  clients.map(c => ({ value: c.id, label: c.name })), true
                )
              ) : associatedClient ? (
                <div className="p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                      <User className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                </div>
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        {associatedClient.name || 'Unknown Contact'}
                      </div>
                      {associatedClient.email && (
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          <a href={`mailto:${associatedClient.email}`} className="hover:text-blue-600">
                            {associatedClient.email}
                          </a>
                        </div>
                      )}
                      {associatedClient.phone && (
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          <a
                            href={`tel:${associatedClientPhoneHref || associatedClient.phone}`}
                            className="hover:text-blue-600"
                          >
                            {associatedClient.phone}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <User className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium mb-2">No primary contact</p>
                  <p className="text-sm mb-4">Associate this artist with a contact to manage their relationship.</p>
                  {currentMode !== 'view' && (
                    <button 
                      onClick={() => setCurrentMode('edit')}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      Add Contact
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Associated Contacts - Only show if artist has contacts */}
            {associatedClient && currentMode === 'view' && (
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                  Contact Details
                </h3>
                {(() => {
                  if (!associatedClient) return null;
                  const primaryContactPhoneHref = associatedClient.phone
                    ? associatedClient.phone.replace(/[^+\d]/g, '')
                    : null;

                  return (
                    <div className="space-y-3">
                      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-3">
                            <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                              <User className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                            <div>
                              <div className="font-medium text-gray-900 dark:text-white">
                                {associatedClient.name}
                              </div>
                              {associatedClient.email && (
                                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                  <a href={`mailto:${associatedClient.email}`} className="hover:text-blue-600">
                                    {associatedClient.email}
                                  </a>
              </div>
            )}
                              {associatedClient.phone && (
                                <div className="text-sm text-gray-600 dark:text-gray-400">
                                  <a
                                    href={`tel:${primaryContactPhoneHref || associatedClient.phone}`}
                                    className="hover:text-blue-600"
                                  >
                                    {associatedClient.phone}
                                  </a>
          </div>
                              )}
                              {associatedClient.company && (
                                <div className="text-sm text-gray-600 dark:text-gray-400">
                                  {associatedClient.company}
              </div>
                        )}
                      </div>
                      </div>
                          <span className="px-2 py-0.5 text-xs bg-blue-100 dark:bg-blue-900/30 
                                         text-blue-700 dark:text-blue-400 rounded-full">
                            Primary
                          </span>
                    </div>
                  </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Similar Artists from Last.fm */}
            {artist?.lastfm_data?.similar?.artist && (
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                  Similar Artists
                </h3>
                <div className="flex flex-wrap gap-2">
                  {artist.lastfm_data.similar.artist.slice(0, 10).map((similar: any, idx: number) => (
                    <span key={idx} className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-800 
                                             rounded-full text-gray-700 dark:text-gray-300">
                      {similar.name}
                    </span>
                ))}
              </div>
              </div>
            )}
          </div>
        );
      }


      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title=""
      showCloseButton={false}
      size="xl"
    >
      <ModalBody>
        {/* Custom Header with Actions */}
        <div className="flex items-center justify-between mb-6 -mt-6 -mx-6 px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
        <div className="flex items-center space-x-4">
          <div className="flex-shrink-0">
              <div 
                onClick={() => setShowImageModal(true)}
                className="cursor-pointer hover:opacity-80 transition-opacity"
                title="Click to view larger image"
              >
            <ArtistAvatar
              artist={artist}
                  size={48}
                  className="ring-2 ring-white dark:ring-gray-800 hover:ring-blue-500 transition-all"
            />
              </div>
          </div>
          <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                  {artist?.name || form.name || 'New Artist'}
            </h2>
                {Object.values(fieldState).some(state => state === 'saving') && (
                  <div className="flex items-center text-sm text-blue-600 dark:text-blue-400">
                    <Loader2 className="w-3 h-3 animate-spin mr-1" />
                    <span className="text-xs">Saving...</span>
                  </div>
                )}
                {Object.values(fieldState).some(state => state === 'error') && (
                  <div className="flex items-center text-sm text-red-600 dark:text-red-400">
                    <X className="w-3 h-3 mr-1" />
                    <span className="text-xs">Error saving</span>
                  </div>
                )}
              </div>
              
              {/* Enhanced metadata display */}
              <div className="flex items-center gap-4 mt-1">
                {(artist?.genre || form.genre) && (
              <span className="text-sm text-gray-600 dark:text-gray-400">
                    {artist?.genre || form.genre}
              </span>
            )}
                
                {spotifyData && (
                  <>
                    <div className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400">
                      <Users className="w-3 h-3" />
                      <span>{formatNumber(spotifyData.followers?.total || 0)} followers</span>
          </div>
                    
                    <div className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400">
                      <BarChart3 className="w-3 h-3" />
                      <span>{spotifyData.popularity}/100 popularity</span>
        </div>
                    
                    {spotifyData.genres && spotifyData.genres.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {spotifyData.genres.slice(0, 2).map(genre => (
                          <span key={genre} className="px-1.5 py-0.5 text-xs bg-green-100 dark:bg-green-900/30 
                                                     rounded text-green-700 dark:text-green-400">
                            {genre}
              </span>
                        ))}
                        {spotifyData.genres.length > 2 && (
                          <span className="text-xs text-gray-500">+{spotifyData.genres.length - 2} more</span>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
          
          {/* Top Right Action Buttons */}
          <div className="flex flex-col items-end gap-2">
            {/* Close Button - Top Right */}
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              aria-label="Close modal"
            >
              <X className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            </button>
            
            {/* Action Buttons Row */}
            <div className="flex items-center gap-2">
              {/* Wizard Navigation for Create Mode */}
              {currentMode === 'create' && currentStep !== 'overview' && (
                <>
                  <button
                    onClick={() => {
                      if (currentStep === 'spotify_url') {
                        onClose();
                      } else if (currentStep === 'review_data') {
                        setCurrentStep('spotify_url');
                      } else if (currentStep === 'contact') {
                        setCurrentStep('review_data');
                      } else if (currentStep === 'releases') {
                        setCurrentStep('contact');
                      } else if (currentStep === 'campaigns') {
                        setCurrentStep('releases');
                      }
                    }}
                    className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white
                             border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    {currentStep === 'spotify_url' ? 'Cancel' : 'Back'}
                  </button>

                  <button
                    onClick={async () => {
                      if (currentStep === 'spotify_url') {
                        // Handled by Import button
                      } else if (currentStep === 'review_data') {
                        setCurrentStep('contact');
                      } else if (currentStep === 'contact') {
                        setCurrentStep('releases');
                        loadReleases();
                      } else if (currentStep === 'releases') {
                        setCurrentStep('campaigns');
                      } else if (currentStep === 'campaigns') {
                        // Create the artist
                        if (onCreate) {
                          setLoadingStep('campaigns');
                          try {
                            const created = await onCreate(form as CreateArtistData);
                            onCreated?.(created);
                            setCurrentStep('overview');
                            toast.success('Artist created successfully!');
                          } catch (error) {
                            console.error('Failed to create artist:', error);
                            toast.error('Failed to create artist');
                          } finally {
                            setLoadingStep(null);
                          }
                        }
                      }
                    }}
                    disabled={
                      loadingStep !== null ||
                      (currentStep === 'spotify_url' && !form.spotify_url) ||
                      (currentStep === 'contact' && !form.client_id)
                    }
                    className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700
                             disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {loadingStep ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {loadingStep === 'campaigns' ? 'Creating...' : 'Loading...'}
                      </>
                    ) : (
                      currentStep === 'campaigns' ? 'Create Artist' : 'Continue'
                    )}
                  </button>
                </>
              )}
              
              {/* Edit/Save buttons for existing artists */}
              {artist && currentStep === 'overview' && (
                <>
              {currentMode === 'view' ? (
                <button
                      onClick={() => {
                        console.log('Edit button clicked, setting mode to edit');
                        setCurrentMode('edit');
                      }}
                      className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 
                               flex items-center font-medium shadow-sm"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                  Edit
                </button>
              ) : (
                    <>
                <button
                        onClick={() => {
                          console.log('Cancel button clicked, reverting to view mode');
                          setCurrentMode('view');
                        }}
                        className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 
                                 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800
                                 font-medium"
                >
                  Cancel
                </button>
                      <button
                        onClick={async () => {
                          await saveAllChanges();
                          if (activeTab === 'contacts') {
                            await refreshArtistData();
                          }
                          setCurrentMode('view');
                        }}
                        className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 
                                 flex items-center font-medium shadow-sm"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Save
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Wizard Progress */}
        {currentMode === 'create' && currentStep !== 'overview' && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              {['spotify_url', 'review_data', 'contact', 'releases', 'campaigns'].map((step, idx) => (
                <React.Fragment key={step}>
                  <div className="flex flex-col items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 
                                  ${currentStep === step 
                                    ? 'border-blue-600 bg-blue-600 text-white'
                                    : currentStep === 'overview' || 
                                      ['spotify_url', 'review_data', 'contact', 'releases', 'campaigns']
                                        .indexOf(currentStep) > 
                                      ['spotify_url', 'review_data', 'contact', 'releases', 'campaigns']
                                        .indexOf(step)
                                      ? 'border-green-600 bg-green-600 text-white'
                                      : 'border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400'
                                  }`}
                    >
                      {currentStep === 'overview' || 
                       ['spotify_url', 'review_data', 'contact', 'releases', 'campaigns']
                         .indexOf(currentStep) > 
                       ['spotify_url', 'review_data', 'contact', 'releases', 'campaigns']
                         .indexOf(step) ? (
                        <CheckCircle className="w-4 h-4" />
                      ) : (
                        <span className="text-sm">{idx + 1}</span>
                      )}
                    </div>
                    <span className="text-xs mt-1 text-gray-600 dark:text-gray-400">
                      {step === 'spotify_url' ? 'Import' :
                       step === 'review_data' ? 'Review' :
                       step === 'contact' ? 'Client' :
                       step === 'releases' ? 'Releases' :
                       'Campaigns'}
                    </span>
                  </div>
                  {idx < 4 && (
                    <div className={`flex-1 h-0.5 mx-2 
                                  ${currentStep === 'overview' || 
                                    ['spotify_url', 'review_data', 'contact', 'releases', 'campaigns']
                                      .indexOf(currentStep) > idx
                                    ? 'bg-green-600'
                                    : 'bg-gray-300 dark:bg-gray-600'
                                  }`} 
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        {/* Status Badges */}
        <div className="flex items-center gap-2 mb-6">
          {artist?.archived && (
            <span className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-800 text-gray-600 
                           dark:text-gray-400 rounded-full">
              Archived
            </span>
          )}
          {/* Archive/Restore Actions */}
          {artist && canEdit && (
            <>
              {artist.archived ? (
                <button
                  onClick={() => onRestore?.(artist.id)}
                  className="px-2 py-1 text-xs border border-green-600 text-green-600 
                           dark:border-green-400 dark:text-green-400 rounded-full 
                           hover:bg-green-50 dark:hover:bg-green-900/20 flex items-center"
                >
                  <RotateCcw className="w-3 h-3 mr-1" />
                  Restore
                </button>
              ) : (
                <button
                  onClick={() => {
                    setConfirmDialog({
                      isOpen: true,
                      title: 'Archive Artist',
                      message: (
                        <div className="space-y-3">
                          <p>Are you sure you want to archive {artist.name}?</p>
                          <p className="text-sm text-gray-500">
                            This will hide the artist from the main list and pause any active campaigns.
                            You can restore the artist later if needed.
                          </p>
                        </div>
                      ),
                      type: 'warning',
                      confirmLabel: 'Archive Artist',
                      onConfirm: () => onArchive?.(artist.id)
                    });
                  }}
                  className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 
                           text-gray-700 dark:text-gray-300 rounded-full 
                           hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center"
                >
                  <Archive className="w-3 h-3 mr-1" />
                  Archive
                </button>
              )}
            </>
          )}
        </div>

        {/* Tabs - Only show when not in create wizard mode */}
        {!(currentMode === 'create' && currentStep !== 'overview') && (
        <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
          <div className="flex space-x-6 overflow-x-auto">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id as TabType);
                    loadTabData(tab.id as TabType);
                  }}
                  className={`flex items-center space-x-2 pb-3 px-1 border-b-2 transition-colors 
                            whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm font-medium">{tab.label}</span>
                  {loadingTab === tab.id && (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
        )}

        {/* Content */}
        <div className="min-h-[400px]">
          {currentMode === 'create' ? renderWizardContent() : renderTabContent()}
        </div>

      </ModalBody>
      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        confirmLabel={confirmDialog.confirmLabel}
        cancelLabel={confirmDialog.cancelLabel}
      />
      
      {/* Image Modal */}
      {(artist?.image_url || form.image_url) && (
        <ImageModal
          isOpen={showImageModal}
          onClose={() => setShowImageModal(false)}
          imageUrl={artist?.image_url || form.image_url || ''}
          artistName={artist?.name || form.name || 'Artist'}
        />
      )}
    </BaseModal>
  );
};
